import { SERVICE_ENTITY_TYPES } from '@/shared/api/openmetadata';
import { removeFqnQuotes } from '@/shared/lib/format';

type BuildServiceUrlParams = {
  serviceType?: string;
  serviceName?: string;
  serviceFqn?: string;
  entityType?: string;
};

/**
 * 서비스 상세 페이지 URL을 생성하는 공통 함수
 *
 * @description
 * OpenMetadata 서비스 엔티티의 URL을 올바른 형식으로 생성합니다.
 * 서비스 URL 형식: `/service/{serviceType}/{serviceName}`
 * 예: `/service/databaseService/gold-iceberg`
 *
 * @param params - URL 생성에 필요한 파라미터
 * @param params.serviceType - 서비스 타입 (예: 'databaseService', 'storageService')
 * @param params.serviceName - 서비스 이름
 * @param params.serviceFqn - 서비스 FQN (형식: 'serviceType/serviceName')
 * @param params.entityType - 엔티티 타입 (SERVICE_ENTITY_TYPES에 포함되는지 확인)
 *
 * @returns 생성된 서비스 URL 또는 null (생성 실패 시)
 *
 * @example
 * ```typescript
 * // serviceType과 serviceName으로 생성
 * buildServiceUrl({
 *   serviceType: 'databaseService',
 *   serviceName: 'gold-iceberg'
 * });
 * // => '/service/databaseService/gold-iceberg'
 *
 * // serviceFqn으로 생성
 * buildServiceUrl({
 *   serviceFqn: 'databaseService/gold-iceberg'
 * });
 * // => '/service/databaseService/gold-iceberg'
 *
 * // entityType과 serviceName으로 생성
 * buildServiceUrl({
 *   entityType: 'databaseService',
 *   serviceName: 'gold-iceberg'
 * });
 * // => '/service/databaseService/gold-iceberg'
 * ```
 */
export const buildServiceUrl = ({
  serviceType,
  serviceName,
  serviceFqn,
  entityType,
}: BuildServiceUrlParams): string | null => {
  if (!serviceName && !serviceFqn) return null;

  if (serviceFqn) {
    const fqnParts = serviceFqn.split('/');
    if (fqnParts.length === 2) {
      const [type, name] = fqnParts;
      return `/service/${type}/${encodeURIComponent(removeFqnQuotes(name))}`;
    }
  }

  if (!serviceName) return null;

  const cleanName = removeFqnQuotes(serviceName);

  if (serviceType) {
    return `/service/${serviceType}/${encodeURIComponent(cleanName)}`;
  }

  if (entityType && SERVICE_ENTITY_TYPES.includes(entityType as (typeof SERVICE_ENTITY_TYPES)[number])) {
    return `/service/${entityType}/${encodeURIComponent(cleanName)}`;
  }

  return null;
};
